import React from 'react';
import { NamedLink } from '../../components';
import { createSlug } from '../../util/urlHelpers';
import { toShort } from '../../util/shortUuid';
import { MapPin, Clock, Users, DollarSign, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const categoryColors = {
  sports: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  arts: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  stem: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  tutoring: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  music: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
  dance: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
  martial_arts: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  aquatics: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  camp: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  other: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
};

const categoryNames = {
  sports: 'Sports',
  arts: 'Arts & Crafts',
  stem: 'STEM',
  tutoring: 'Tutoring',
  music: 'Music',
  dance: 'Dance',
  martial_arts: 'Martial Arts',
  aquatics: 'Aquatics',
  camp: 'Camp',
  other: 'Other',
};

const formatPrice = (activity) => {
  if (activity.priceType === 'free') return 'Free';
  const cents = activity.priceAmountCents || activity.priceMin;
  if (!cents) return 'Contact for pricing';
  const max = activity.priceMax;
  if (max && max !== cents) {
    return `$${(Number(cents) / 100).toFixed(0)} - $${(Number(max) / 100).toFixed(0)}`;
  }
  return `$${(Number(cents) / 100).toFixed(0)}`;
};

const formatAgeRange = (activity) => {
  if (!activity.ageMin && !activity.ageMax) return null;
  if (activity.ageMin && activity.ageMax) return `Ages ${activity.ageMin}-${activity.ageMax}`;
  if (activity.ageMin) return `Ages ${activity.ageMin}+`;
  return `Up to age ${activity.ageMax}`;
};

const ActivityCard = ({ activity, filterParams }) => {
  const { organization, location, sessions } = activity;
  const nextSession = sessions?.[0];
  const isVerified = organization?.verificationStatus === 'verified';
  const category = activity.category || 'other';

  return (
    <NamedLink
      name="ActivityDetailPage"
      params={{ id: toShort(activity.id), slug: createSlug(activity.title || '') }}
      className="no-underline"
    >
      <Card className="overflow-visible cursor-pointer transition-all hover:shadow-lg group h-full rounded-2xl">
        <div className="aspect-video bg-muted relative overflow-hidden rounded-t-2xl">
          {activity.photos && activity.photos.length > 0 ? (
            <img
              src={activity.photos[0]}
              alt={activity.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <span className="text-4xl">
                {(categoryNames[category] || 'A').charAt(0)}
              </span>
            </div>
          )}
          <Badge
            className={`absolute top-2 left-2 ${categoryColors[category] || categoryColors.other}`}
          >
            {categoryNames[category] || 'Activity'}
          </Badge>
        </div>
        <CardContent className="p-4 space-y-3">
          <div className="space-y-1">
            <h3 className="font-semibold text-lg line-clamp-1 group-hover:text-primary transition-colors">
              {activity.title}
            </h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {organization && (
                <span className="flex items-center gap-1">
                  {isVerified && (
                    <CheckCircle className="h-3 w-3 text-primary" />
                  )}
                  {organization.name}
                </span>
              )}
            </div>
          </div>

          <p className="text-sm text-muted-foreground line-clamp-2">
            {activity.description || 'No description available'}
          </p>

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {location.neighborhood || location.city}
              </span>
            )}
            {formatAgeRange(activity) && (
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {formatAgeRange(activity)}
              </span>
            )}
            {nextSession && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {nextSession.startTime?.toString().slice(0, 5)}
              </span>
            )}
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <span className="flex items-center gap-1 font-semibold">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              {formatPrice(activity)}
            </span>
            {sessions && sessions.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {sessions.length} session{sessions.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </NamedLink>
  );
};

export default ActivityCard;
